// The frame for the sign-in pages (Figma "Sign-in & app states · web"): the
// brand panel on the left from lg up, the form on the right. StatusPanel is
// the centred one-message state (check your email, link expired, password
// changed, signing you in); AppStatePage is the same message on a page of its
// own with no brand panel (loading, reconnecting, 404). Sign-in is dark-only by
// design (as on mobile); AppStatePage follows the app's theme.
import React from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { cn } from "../../lib/utils";
import AithloIcon from "../icons/AithloIcon";
import { TERMS_URL } from "../../lib/aiConsent";

const PROMISES = [
  "Swim, bike, run and strength in one plan",
  "See every change before it lands, and ask why",
  "Sessions on your watch and in Aithlo Shift",
];

export function Wordmark({ className }) {
  return (
    <span className={cn("inline-flex items-center gap-2 text-text-primary", className)}>
      <AithloIcon size={24} className="text-accent-default" />
      <span className="font-display text-[26px] font-bold leading-none">aithlo</span>
    </span>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="dark flex min-h-screen w-full bg-bg-canvas font-body text-text-body">
      <aside className="hidden w-[42%] max-w-[600px] shrink-0 flex-col gap-6 bg-bg-panel px-16 py-14 lg:flex">
        <Link to="/login" aria-label="Aithlo" className="self-start"><Wordmark /></Link>
        <div className="flex-1" />
        <p className="m-0 type-display-hero text-text-primary" style={{ fontSize: 44 }}>
          A coach that changes the plan when life does.
        </p>
        <ul className="m-0 flex list-none flex-col gap-6 p-0">
          {PROMISES.map((p) => (
            <li key={p} className="flex items-start gap-3 type-body text-text-body">
              <Check size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
        <p className="m-0 mt-10 type-caption text-text-tertiary">
          © {new Date().getFullYear()} Aithlo ·{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-text-tertiary hover:text-text-secondary">Terms</a>
        </p>
      </aside>
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-10 lg:hidden"><Wordmark /></div>
          {children}
        </div>
      </main>
    </div>
  );
}

/** The page title and its line: 16 apart, and 16 above the form (Register: 14, small). */
export function AuthHeading({ title, subtitle, small = false }) {
  return (
    <div className={cn("flex flex-col", small ? "mb-3.5 gap-3.5" : "mb-4 gap-4")}>
      <h1 className="m-0 type-title-session text-text-primary">{title}</h1>
      {subtitle ? <p className={cn("m-0 text-text-secondary", small ? "type-body-small" : "type-body")}>{subtitle}</p> : null}
    </div>
  );
}

export function SwitchLink({ prompt, to, label }) {
  return (
    <p className="m-0 flex flex-wrap justify-center gap-1.5 type-body-small">
      <span className="text-text-tertiary">{prompt}</span>
      <Link to={to} className="font-semibold text-accent-default hover:text-accent-hover">{label}</Link>
    </p>
  );
}

export const Spinner = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" aria-hidden="true" className="animate-spin">
    <circle cx="14" cy="14" r="11" strokeWidth="3" className="stroke-border-default" />
    <path d="M14 3a11 11 0 0 1 11 11" strokeWidth="3" strokeLinecap="round" className="stroke-accent-default" />
  </svg>
);

/**
 * One message, centred: a round mark, the title, a line and the actions,
 * 16 apart. icon: "check" (teal tint), "x" / "spinner" (neutral), "404" (teal tint).
 */
export function StatusPanel({ icon, title, body, children, footnote }) {
  const tinted = icon === "check" || icon === "404";
  return (
    <div className="flex flex-col items-center gap-4 text-center" aria-live="polite">
      <span className={cn("inline-flex items-center justify-center rounded-full p-[18px]",
        icon === "404" ? "bg-accent-tint text-accent-default" : tinted ? "bg-accent-tint text-text-secondary" : "bg-bg-surface text-text-secondary")}>
        {icon === "spinner" ? <Spinner />
          : icon === "404" ? <span className="type-label-section">404</span>
          : icon === "check" ? <Check size={28} strokeWidth={2} aria-hidden="true" />
          : <X size={28} strokeWidth={2} aria-hidden="true" />}
      </span>
      <h1 className="m-0 type-title-session text-text-primary" style={{ fontSize: 32 }}>{title}</h1>
      {body ? <p className="m-0 type-body text-text-secondary">{body}</p> : null}
      {children ? <div className="flex w-full flex-col gap-4">{children}</div> : null}
      {footnote ? <p className="m-0 type-caption text-text-tertiary">{footnote}</p> : null}
    </div>
  );
}

/** A StatusPanel on a page of its own (no brand panel): loading, reconnecting, 404. */
export function AppStatePage({ children, className }) {
  return (
    <div className={cn("flex min-h-screen w-full items-center justify-center bg-bg-canvas px-6 py-12 font-body text-text-body", className)}>
      <div className="w-full max-w-[400px]">{children}</div>
    </div>
  );
}

/** A select drawn as a Field box (Register's country). */
export const SELECT_CLASS =
  "min-h-[50px] w-full appearance-none rounded-chip border border-border-default bg-bg-canvas px-3.5 type-body text-text-primary focus:border-[1.5px] focus:border-accent-default focus:outline-none";
